import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Login successful!');
      
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const testimonials = [
    '⭐ TOP RATED BY 5,000+ USERS',
    '🔥 TRENDING TODAY',
    '✅ 100+ NEW PROMPTS ADDED',
    '🏆 #1 AI RESOURCE 2025',
    '⚡ INSTANT ACCESS',
    '🚀 BOOST YOUR PRODUCTIVITY',
    '🎯 10X YOUR OUTPUT'
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background: '#0a0a0a'}}>
      {/* Blurred Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1920&h=1080&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(40px) brightness(0.3)'
        }}></div>
      </div>

      {/* Newspaper Collage Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="grid grid-cols-6 gap-4 p-8 rotate-12 scale-150" style={{filter: 'blur(8px)'}}>
          {[...Array(24)].map((_, i) => (
            <div key={i} className="bg-white/5 h-32 rounded-lg"></div>
          ))}
        </div>
      </div>

      {/* Top Marquee Banner */}
      <div className="absolute top-0 left-0 right-0 z-10 overflow-hidden" style={{
        background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
        height: '48px'
      }}>
        <div className="flex items-center h-full whitespace-nowrap animate-marquee">
          {[...testimonials, ...testimonials, ...testimonials].map((text, i) => (
            <span key={i} className="inline-block px-8 text-black font-bold text-lg">
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom Marquee Banner */}
      <div className="absolute bottom-0 left-0 right-0 z-10 overflow-hidden" style={{
        background: 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)',
        height: '48px'
      }}>
        <div className="flex items-center h-full whitespace-nowrap animate-marquee-reverse">
          {[...testimonials, ...testimonials, ...testimonials].map((text, i) => (
            <span key={i} className="inline-block px-8 text-black font-bold text-lg">
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* Back Home Link */}
      <div className="absolute top-16 left-8 z-20">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-lg font-bold" style={{fontFamily: 'Outfit', color: '#f59e0b'}}>AIPedia</span>
        </Link>
      </div>

      {/* Glassmorphism Login Card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-24">
        <Card 
          className="w-full max-w-md border-white/20" 
          style={{
            background: 'rgba(23, 23, 23, 0.7)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px 0 rgba(245, 158, 11, 0.2)'
          }}
          data-testid="login-card"
        >
          <CardHeader className="space-y-2">
            <CardTitle className="text-3xl font-bold text-center" style={{fontFamily: 'Outfit', color: '#fafafa'}}>Welcome Back</CardTitle>
            <CardDescription className="text-center text-muted-foreground">Login to access your AI prompts</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-black/40 border-white/20"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-black/40 border-white/20"
                  data-testid="login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-semibold" data-testid="register-link">
                Register here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <style jsx="true">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes marquee-reverse {
          0% { transform: translateX(-33.333%); }
          100% { transform: translateX(0); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee-reverse {
          animation: marquee-reverse 30s linear infinite;
        }
      `}</style>
    </div>
  );
}
